import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { SEO } from "@/components/seo/SEO";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useExamContext } from "@/contexts/ExamContext";
import {
  Search, Clock, Brain, ArrowRight,
  Target, Zap, BarChart3, BookOpen, Award,
  TrendingUp, FileQuestion, GraduationCap, Stethoscope,
  Check, X, Lock,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Index = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { setSelectedExamSlug } = useExamContext();
  const [examSearch, setExamSearch] = useState("");

  const examPacks = [
    {
      slug: "medical-student",
      name: "Medical Student MCQs",
      questions: "2,467",
      topics: 17,
      currency: "$",
      price: "9.99",
      audience: "Medical Students worldwide",
      color: "hsl(var(--primary))",
      available: true,
    },
    {
      slug: "smle",
      name: "SMLE Preparation",
      questions: "7,651",
      topics: 25,
      currency: "SAR ",
      price: "50",
      audience: "Saudi Medical Licensing Exam candidates",
      color: "#16a34a",
      available: true,
    },
  ];

  const comingSoon = [
    { name: "PLAB Preparation", audience: "UK Professional & Linguistic Assessments Board" },
    { name: "MRCP Preparation", audience: "Membership of the Royal Colleges of Physicians" },
  ];

  const allSearchable = [
    ...examPacks.map((e) => ({ name: e.name, slug: e.slug, questions: e.questions, available: true })),
    ...comingSoon.map((e) => ({ name: e.name, slug: "", questions: "—", available: false })),
  ];

  const filtered = examSearch
    ? allSearchable.filter((e) => e.name.toLowerCase().includes(examSearch.toLowerCase()))
    : [];

  const handleStartRevising = (slug: string) => {
    setSelectedExamSlug(slug);
    navigate(user ? `/study?exam=${slug}` : "/auth");
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="QuickMedicalReview — Pass Your Medical Exams First Time"
        description="10,000+ expert MCQs for Medical Students, SMLE, PLAB & MRCP. Detailed explanations, mock exams, and performance analytics."
        path="/"
      />

      {/* ── Hero ── */}
      <section className="medical-hero min-h-[70vh] flex items-center justify-center py-16 lg:py-24">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="secondary" className="bg-white/15 text-white/90 border-white/20 backdrop-blur-md mb-6">
              <Stethoscope className="w-4 h-4 mr-2" />
              Trusted by Medical Professionals
            </Badge>

            <h1 className="text-4xl lg:text-6xl font-bold font-manrope leading-[1.1] mb-5">
              Pass Your Medical Exams
              <span className="block mt-2 text-white/90">First Time</span>
            </h1>
            <p className="text-lg lg:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed mb-10">
              High-impact questions developed by exam revision experts. Track your performance, compare with peers, and focus on what matters most.
            </p>

            {/* Search */}
            <div className="max-w-xl mx-auto mb-10 relative">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  placeholder="Find my exam..."
                  value={examSearch}
                  onChange={(e) => setExamSearch(e.target.value)}
                  className="pl-12 pr-4 py-6 text-lg rounded-xl bg-white text-foreground shadow-elevated border-0"
                />
              </div>
              {examSearch && filtered.length > 0 && (
                <div className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-elevated border overflow-hidden z-50">
                  {filtered.map((item) => (
                    <button
                      key={item.name}
                      disabled={!item.available}
                      onClick={() => {
                        if (item.available) {
                          handleStartRevising(item.slug);
                          setExamSearch("");
                        }
                      }}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors text-left text-foreground disabled:opacity-50"
                    >
                      <span className="font-medium">{item.name}</span>
                      <span className="text-sm text-muted-foreground">
                        {item.available ? `${item.questions} questions` : "Coming Soon"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Hero Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
              {[
              { icon: FileQuestion, value: "10,000+", label: "Practice MCQs" },
                { icon: BookOpen, value: "40+", label: "Topics Covered" },
              ].map(({ icon: Icon, value, label }) => (
                <div key={label} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white/10 backdrop-blur-sm">
                  <Icon className="h-8 w-8 text-white" />
                  <span className="text-2xl font-bold text-white">{value}</span>
                  <span className="text-sm text-white/70">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Our Exam Packs ── */}
      <section className="py-16 bg-background">
        <div className="section-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold font-manrope mb-4">Our Exam Packs</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose your exam and start revising with expert-quality questions
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto">
            {examPacks.map((exam) => (
              <Card key={exam.slug} className="overflow-hidden group hover:shadow-lg transition-all">
                <div className="h-2" style={{ background: exam.color }} />
                <CardContent className="p-8 space-y-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-2xl font-bold font-manrope">{exam.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{exam.audience}</p>
                    </div>
                    <GraduationCap className="h-7 w-7 text-primary flex-shrink-0" />
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><FileQuestion className="h-4 w-4" />{exam.questions} questions</span>
                    <span className="flex items-center gap-1"><BookOpen className="h-4 w-4" />{exam.topics} topics</span>
                  </div>

                  <div className="text-sm">
                    <span className="text-muted-foreground">From </span>
                    <span className="font-bold text-primary text-2xl">{exam.currency}{exam.price}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button className="flex-1 bg-gradient-medical" onClick={() => handleStartRevising(exam.slug)}>
                      Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/pricing")}>
                      View Pricing
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Coming Soon */}
          <div className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto mt-8">
            {comingSoon.map((exam) => (
              <Card key={exam.name} className="opacity-60 border-dashed">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold font-manrope flex items-center gap-2">
                      <Lock className="h-4 w-4" /> {exam.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">{exam.audience}</p>
                  </div>
                  <Badge variant="secondary">Coming Soon</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="py-16 bg-muted/30">
        <div className="section-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold font-manrope mb-4">How It Works</h2>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 max-w-5xl mx-auto">
            {[
              { step: "1", icon: GraduationCap, title: "Choose Your Exam", desc: "Select Medical Student, SMLE, or another supported exam." },
              { step: "2", icon: Target, title: "Practice MCQs", desc: "Work through expert-curated questions with detailed explanations." },
              { step: "3", icon: BarChart3, title: "Track Progress", desc: "Monitor performance and identify weak areas with analytics." },
              { step: "4", icon: Award, title: "Pass Your Exam", desc: "Walk into your exam with confidence and pass first time." },
            ].map(({ step, icon: Icon, title, desc }) => (
              <div key={step} className="text-center space-y-4">
                <div className="relative mx-auto w-16 h-16">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Icon className="h-8 w-8 text-primary" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                    {step}
                  </div>
                </div>
                <h3 className="text-lg font-bold font-manrope">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-16">
        <div className="section-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold font-manrope mb-4">How We Help You Pass</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
            {[
              { icon: Zap, title: "High Impact Questions", desc: "Targeting the most frequently tested topics and common exam pitfalls." },
              { icon: Clock, title: "Timed Mock Exams", desc: "Simulate real exam conditions to build confidence and time management." },
              { icon: BarChart3, title: "Performance Analytics", desc: "Peer comparison and detailed analytics to track your progress." },
              { icon: BookOpen, title: "Detailed Explanations", desc: "Every question includes comprehensive explanations for deep learning." },
              { icon: Brain, title: "AI-Powered Coaching", desc: "Smart recommendations that focus on your weakest areas." },
              { icon: TrendingUp, title: "Adaptive Learning", desc: "Questions adjust to your level for maximum learning efficiency." },
            ].map(({ icon: Icon, title, desc }) => (
              <Card key={title} className="group hover:shadow-md transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 font-manrope">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── Free vs Premium ── */}
      <section className="py-16">
        <div className="section-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold font-manrope mb-4">Free vs Premium</h2>
            <p className="text-lg text-muted-foreground">See what you get with a subscription</p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="grid grid-cols-3 gap-0 rounded-2xl border overflow-hidden">
              <div className="p-4 bg-muted font-bold text-sm">Feature</div>
              <div className="p-4 bg-muted font-bold text-sm text-center">Free</div>
              <div className="p-4 bg-primary/10 font-bold text-sm text-center text-primary">Premium</div>

              {[
                ["Browse exam info", true, true],
                ["3-day free trial", true, true],
                ["Full question bank", false, true],
                ["Detailed explanations", false, true],
                ["Progress tracking", false, true],
                ["Mock exams", false, true],
                ["Performance analytics", false, true],
                ["AI-powered coaching", false, true],
                ["Study plan generator", false, true],
              ].map(([feature, free, sub], i) => (
                <div key={i} className="contents">
                  <div className="p-3 border-t text-sm">{feature as string}</div>
                  <div className="p-3 border-t flex justify-center">
                    {free ? <Check className="h-5 w-5 text-emerald-600" /> : <X className="h-5 w-5 text-muted-foreground/40" />}
                  </div>
                  <div className="p-3 border-t bg-primary/5 flex justify-center">
                    {sub ? <Check className="h-5 w-5 text-emerald-600" /> : <X className="h-5 w-5 text-muted-foreground/40" />}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Button size="lg" className="bg-gradient-medical" onClick={() => navigate("/pricing")}>
                View Pricing Plans <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>


      {/* ── FAQ ── */}
      <section className="py-16">
        <div className="section-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold font-manrope mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-2xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {[
                { q: "What exams do you cover?", a: "We currently offer Medical Student MCQs (2,467 questions across 17 topics) and SMLE Preparation (7,651 questions across 25 topics). PLAB and MRCP are coming soon." },
                { q: "How many questions are available?", a: "Over 10,000 expert-curated MCQs in total. Each exam has its own dedicated question bank developed by medical professionals." },
                { q: "Can I switch between exams?", a: "Yes! Each exam requires its own subscription. You can subscribe to multiple exams and use the exam switcher in the header to toggle between them." },
                { q: "What payment methods do you accept?", a: "We accept all major credit and debit cards via Stripe. SMLE subscriptions are billed in Saudi Riyal (SAR), while Medical Student subscriptions are in US Dollars (USD)." },
                { q: "Is there a free trial?", a: "Yes! Every subscription plan includes a 3-day free trial. You get full access to the question bank, mock exams, and analytics during your trial." },
                { q: "What currencies do you support?", a: "We support USD for Medical Student subscriptions and SAR (Saudi Riyal) for SMLE subscriptions. More currencies will be added as we expand to PLAB (GBP) and MRCP." },
              ].map(({ q, a }, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left">{q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="medical-hero py-16">
        <div className="section-container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold font-manrope mb-4">Start Your Exam Preparation Today</h2>
            <p className="text-lg text-white/80 mb-8">Join the platform trusted by medical professionals worldwide.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="medical-btn-primary px-8 py-4 text-lg h-auto" onClick={() => navigate(user ? "/study" : "/auth")}>
                {user ? "Continue Studying" : "Create Free Account"} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" className="medical-btn-secondary px-8 py-4 text-lg h-auto" onClick={() => navigate("/pricing")}>
                View Pricing
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;
